import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useGetOrganisationLocations, useLegacyGetOrganisationChargePoints } from '../../../../app/ApiGen';
import { useCurrentOrganisation } from '../../../../app/CurrentOrganisationProvider';
import { NoChargePoints } from '../../../../components/NoContentPlaceholder/NoChargePoints';
import { NoLocations } from '../../../../components/NoContentPlaceholder/NoLocations';
import { Loading, Section, Workspace } from '../../../../design-system';
import { selectDerivedState } from '../../../../store/root';
import { normalise } from '../../../../utils/request';
import { ConnectorStatuses } from './components/ConnectorStatuses';
import { LocationsMap } from './components/LocationsMap';
import { OrganisationSummary } from './components/OrganisationSummary';

const OverviewView: React.FC = () => {
  const { loading, organisation } = useCurrentOrganisation();
  const {
    params: { slug: orgSlug },
  } = useRouteMatch<{ slug: string }>();
  const { isDriver, isAdmin, isEvnexAdmin } = selectDerivedState({ organisation: { loading, current: organisation } });

  const { loading: loadingChargePoints, data: chargePoints } = useLegacyGetOrganisationChargePoints({
    orgSlug,
  });

  const { loading: loadingLocations, data } = useGetOrganisationLocations({
    orgSlug,
  });

  if (loading || loadingChargePoints || loadingLocations || !chargePoints || !organisation || !data) {
    return (
      <Workspace>
        <Loading />
      </Workspace>
    );
  }

  const locations = normalise(data);

  const locationsAndNoChargePoints = locations.length > 0 && chargePoints.data.items.length === 0;
  if (locationsAndNoChargePoints) {
    return (
      <Workspace maxWidth="lg">
        <Section>
          <Card>
            <Grid container direction="column">
              <Grid container item xs={12}>
                <Grid
                  item
                  xs={12}
                  style={{ padding: 30 }}
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography variant="h2" gutterBottom component="h3">
                    Welcome
                  </Typography>
                  {(isAdmin || isEvnexAdmin) && (
                    <Typography variant="h6" gutterBottom>
                      To get started, add a charge point
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Grid container item direction="row" xs={12}>
                <Grid
                  container
                  item
                  xs={12}
                  direction="column"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ paddingBottom: isDriver ? 30 : 0 }}
                >
                  <NoChargePoints orgSlug={orgSlug} isDriver={isDriver} />
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Section>
      </Workspace>
    );
  }

  const noLocationsAndNoChargePoints = locations.length === 0 && chargePoints.data.items.length === 0;
  if (noLocationsAndNoChargePoints) {
    return (
      <Workspace maxWidth="lg">
        <Section>
          <Card>
            <Grid container direction="column">
              <Grid container item xs={12}>
                <Grid
                  item
                  xs={12}
                  style={{ padding: 30 }}
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography variant="h2" gutterBottom component="h3">
                    Welcome
                  </Typography>
                  {(isAdmin || isEvnexAdmin) && (
                    <Typography variant="h6" gutterBottom>
                      To get started, add a charge point or location below
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Grid container item direction="row" xs={12}>
                {isDriver ? (
                  <Grid
                    container
                    item
                    xs={12}
                    direction="column"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ paddingBottom: 30 }}
                  >
                    <NoChargePoints orgSlug={orgSlug} isDriver={isDriver} />
                  </Grid>
                ) : (
                  <>
                    <Grid container item xs={6} direction="column" justifyContent="space-between" alignItems="center">
                      <NoChargePoints orgSlug={orgSlug} isDriver={isDriver} />
                    </Grid>
                    <Grid container item xs={6} direction="column" justifyContent="space-between" alignItems="center">
                      <NoLocations orgSlug={orgSlug} isDriver={isDriver} />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </Card>
        </Section>
      </Workspace>
    );
  }

  return (
    <>
      <Grid container direction="column">
        <Grid item>
          <LocationsMap />
        </Grid>
        <Grid container item>
          <Workspace maxWidth="lg">
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={8}>
                <OrganisationSummary orgSlug={orgSlug} orgId={organisation.id} />
              </Grid>
              <Grid item xs={12} md={12} lg={4}>
                <ConnectorStatuses organisation={organisation} />
              </Grid>
            </Grid>
          </Workspace>
        </Grid>
      </Grid>
    </>
  );
};

export default OverviewView;
